import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiTarget, FiAward, FiHeart } = FiIcons;

const stats = [
  { icon: FiUsers, number: '7+', label: 'Over 7 years combined experience in design & SEO' },
  { icon: FiTarget, number: '50+', label: 'Over 50 websites designed for local businesses in Buckinghamshire & beyond' },
  { icon: FiAward, number: '70%', label: 'Over 70% repeat business and long-term support clients' },
  { icon: FiHeart, number: '100%', label: '100% of our website are mobile responsive across all devices' }
];

const values = [
  {
    title: 'Innovation First',
    description: 'We stay ahead of the curve by embracing cutting-edge technologies and creative solutions.'
  },
  {
    title: 'Client-Centric',
    description: 'Your success is our success. We work closely with you to understand and exceed your expectations.'
  },
  {
    title: 'Quality Driven',
    description: 'We never compromise on quality, delivering pixel-perfect designs and robust, scalable solutions.'
  },
  {
    title: 'Transparent Process',
    description: 'Clear communication and transparent processes ensure you\'re always in the loop.'
  }
];

const About = () => {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6">
            About us
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We're a passionate team of designers, developers, and strategists dedicated to creating digital experiences that make a difference.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-slate-900 mb-6">
              Building digital solutions that drive real results
            </h3>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              Started as a small team with big dreams, we've grown into a full-service digital agency that helps businesses of all sizes achieve their online goals. Our approach combines strategic thinking with creative execution to deliver results that matter.
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              We believe in the power of collaboration, innovation, and attention to detail. Every project is an opportunity to create something extraordinary and push the boundaries of what's possible.
            </p>
            
            {/* Values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <div key={index} className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">{value.title}</h4>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop" 
              alt="Team collaboration" 
              className="rounded-2xl shadow-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-6 rounded-2xl shadow-xl">
              <div className="text-2xl font-bold">7+ Years</div>
              <div className="text-sm opacity-90">of Excellence</div>
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 bg-slate-50 rounded-2xl p-8"
        >
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={stat.icon} className="text-orange-600 text-2xl" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-2">{stat.number}</div>
              <div className="text-slate-600 text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;